import React, { useCallback } from 'react';
import { ActionButton, DialogTrigger, Divider, StatusLight, } from '@adobe/react-spectrum';
import { Flex } from '@react-spectrum/layout';
import { View } from '@react-spectrum/view';
import ChevronLeft from '@spectrum-icons/workflow/ChevronLeft';
import ChevronRight from '@spectrum-icons/workflow/ChevronRight';
import InfoOutline from '@spectrum-icons/workflow/InfoOutline';
import Settings from '@spectrum-icons/workflow/Settings';
import { Twitter } from 'app/assets/brands/Twitter';
import { EditorActions } from 'app/stores/editor/actions';
import { ProjectActions } from 'app/stores/project/actions';
import { getActiveTool, getCurrentPageIndex, getPagesCount, } from 'app/stores/selectors';
import { useAppDispatch } from 'app/stores/store';
import { AboutDialog } from 'app/views/components/dialogs/AboutDialog';
import { IconButton } from 'app/views/components/footer/IconButton';
import { SchemeSwitch } from 'app/views/components/footer/SchemeSwitch';
import { Zoom } from 'app/views/components/footer/Zoom';
import { Text } from 'app/views/components/typography/Text';
import { useConnect } from 'app/views/hooks/useConnect';
import { analytics } from 'app/utils/analytics';
import { snapToPixel } from 'app/utils/math';
import { useOnline } from '../hooks/useOnline';
const VR = () => (React.createElement(Divider, { orientation: "vertical", size: "S", marginStart: "size-100", marginEnd: "size-100" }));
const Online = () => {
    const isOnline = useOnline();
    if (!isOnline) {
        return React.createElement(StatusLight, { variant: "yellow" }, "Offline");
    }
    return null;
};
const AboutButton = (props) => (React.createElement(IconButton, { "data-test-id": "about-trigger", onPress: props.onPress },
    React.createElement(InfoOutline, { size: "XS" })));
const SettingsButton = () => {
    const dispatch = useAppDispatch();
    const onPress = useCallback(() => {
        dispatch(EditorActions.TEMPORARYsetSettingsOpened(true));
    }, [dispatch]);
    return (React.createElement(IconButton, { "data-test-id": "settings-trigger", onPress: onPress },
        React.createElement(Settings, { size: "XS" })));
};
const TwitterButton = () => (React.createElement("a", { "data-test-id": "twitter-button", href: "https://twitter.com/annotely", target: "_blank", rel: "noreferrer", style: { textDecoration: 'none', lineHeight: 0, padding: '2px' } },
    React.createElement(Twitter, null)));
const SizeInfo = () => {
    const { width, height } = useConnect(state => {
        var _a, _b;
        const imageSize = state.viewport.virtual;
        switch (state.editor.activeController) {
            case 'image-crop': {
                const unifiedArea = (_b = (_a = state.editor.controllerData) === null || _a === void 0 ? void 0 : _a.unifiedArea) !== null && _b !== void 0 ? _b : { width: 1, height: 1 };
                return {
                    width: snapToPixel(imageSize.width * unifiedArea.width),
                    height: snapToPixel(imageSize.height * unifiedArea.height),
                };
            }
            default:
                return {
                    width: imageSize.width,
                    height: imageSize.height,
                };
        }
    });
    return (React.createElement(Text, { size: "XS" },
        width,
        " x ",
        height));
};
const Navigation = (props) => {
    const { index, count } = props;
    const dispatch = useAppDispatch();
    const onNext = useCallback(() => {
        dispatch(ProjectActions.gotoNextPage());
    }, [dispatch]);
    const onPrev = useCallback(() => {
        dispatch(ProjectActions.gotoPrevPage());
    }, [dispatch]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ActionButton, { isQuiet: true, onPress: onPrev },
            React.createElement(ChevronLeft, { slot: "icon" })),
        React.createElement(Text, { size: "XS" },
            "Slide ",
            index + 1,
            "/",
            count),
        React.createElement(ActionButton, { isQuiet: true, onPress: onNext },
            React.createElement(ChevronRight, { slot: "icon" }))));
};
export function FooterView() {
    const stateProps = useConnect(state => {
        var _a;
        return ({
            activeTool: (_a = getActiveTool(state)) === null || _a === void 0 ? void 0 : _a.label,
            currentPageIndex: getCurrentPageIndex(state),
            pagesCount: getPagesCount(state),
        });
    });
    const { activeTool, currentPageIndex, pagesCount } = stateProps;
    const hasAnyPages = pagesCount > 0;
    const hasMultiplePages = hasAnyPages && pagesCount > 1;
    return (React.createElement(View, { "data-test-id": "footer-view", height: "size-400", borderTopWidth: "thin", backgroundColor: "gray-50", borderTopColor: "gray-200" },
        React.createElement(Flex, { marginX: "size-75", height: "100%", direction: "row", alignItems: "center", justifyContent: "start" },
            React.createElement(SchemeSwitch, null),
            React.createElement(Online, null),
            React.createElement(Flex, { flex: "1 1", height: "100%", direction: "row", alignItems: "center", justifyContent: "end" },
                hasMultiplePages && (React.createElement(React.Fragment, null,
                    React.createElement(Navigation, { index: currentPageIndex, count: pagesCount }),
                    React.createElement(VR, null))),
                hasAnyPages && (React.createElement(React.Fragment, null,
                    activeTool && (React.createElement(React.Fragment, null,
                        React.createElement(Text, { size: "XS" }, activeTool),
                        React.createElement(VR, null))),
                    React.createElement(Zoom, null),
                    React.createElement(VR, null),
                    React.createElement(SizeInfo, null),
                    React.createElement(VR, null))),
                React.createElement(SettingsButton, null),
                React.createElement(VR, null),
                React.createElement(DialogTrigger, { onOpenChange: isOpen => isOpen && analytics({ action: 'About' }) },
                    React.createElement(AboutButton, null),
                    React.createElement(AboutDialog, null)),
                React.createElement(VR, null),
                React.createElement(TwitterButton, null)))));
}
